import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faBookOpen,
  faExclamationTriangle,
  faHospital,
  faRotateRight,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { BASE_URL } from "../../config";
import { MarkdownSpecsContent } from "../Member/BatchFileSpecsModal";

const LOA_PDF_SPECS_ENDPOINT = `${BASE_URL}/generate-loa/loa-document/specs`;

const LoaPdfGenerationSpecsPage = () => {
  const navigate = useNavigate();
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadSpecs = () => {
    setLoading(true);
    setError("");

    axios
      .get(LOA_PDF_SPECS_ENDPOINT, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
          Accept: "text/markdown,text/plain,*/*",
        },
        responseType: "text",
        transformResponse: [(data) => data],
      })
      .then((response) => {
        setContent(typeof response.data === "string" ? response.data : "");
      })
      .catch((requestError) => {
        if (requestError.response?.status === 401) {
          window.location.href = "/login";
          return;
        }

        const responseText = requestError?.response?.data;
        setError(
          typeof responseText === "string" && responseText.trim()
            ? responseText
            : "Unable to load LOA PDF generation specs."
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    loadSpecs();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="mx-auto max-w-6xl">
        <div className="mb-4 rounded-xl border border-gray-200 bg-white/95 px-5 py-4 shadow-sm backdrop-blur-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700">
                <FontAwesomeIcon icon={faBookOpen} />
              </div>
              <div className="min-w-0">
                <h1 className="truncate text-xl font-bold text-gray-900">
                  LOA PDF Generation Specs
                </h1>
                <p className="text-xs text-gray-500">
                  Version 2 service flow and database write behavior
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                onClick={() => navigate("/transactions")}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50"
              >
                <FontAwesomeIcon icon={faArrowLeft} />
                Transactions
              </button>
              <button
                type="button"
                onClick={() => navigate("/generate-loa-transaction")}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700"
              >
                <FontAwesomeIcon icon={faHospital} />
                Generate LOA
              </button>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white px-5 py-4 shadow-sm">
          {loading ? (
            <div className="flex min-h-[420px] flex-col items-center justify-center gap-3 text-gray-600">
              <FontAwesomeIcon icon={faSpinner} className="text-2xl text-indigo-600 animate-spin" />
              <p className="text-sm font-medium">Loading specs...</p>
            </div>
          ) : error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
              <div className="flex items-start gap-3">
                <FontAwesomeIcon icon={faExclamationTriangle} className="mt-1 text-red-600" />
                <div className="flex-1">
                  <p className="text-sm font-semibold">Specs unavailable</p>
                  <p className="mt-1 text-sm">{error}</p>
                  <button
                    type="button"
                    onClick={loadSpecs}
                    className="mt-3 inline-flex items-center gap-2 rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700"
                  >
                    <FontAwesomeIcon icon={faRotateRight} />
                    Retry
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <MarkdownSpecsContent content={content} className="mx-auto max-w-5xl pb-4" />
          )}
        </div>
      </div>
    </div>
  );
};

export default LoaPdfGenerationSpecsPage;
